import React from 'react';
import { DataGrid } from '@weave-mui/data-grid';
import {
  Box,
  IconButton,
  Tooltip,
  Link,
  Typography,
} from '@weave-mui/material';
import {
  EditM as EditIcon,
  TrashM as TrashIcon,
  CopyM as CopyIcon,
  ExportMoveM as MoveIcon,
  PlaybackPlayM as PlayIcon,
} from '@weave-mui/icons-weave';

/**
 * HostingTable component displays test cases in a DataGrid table format
 * Provides functionality for viewing, selecting, and managing test cases with
 * support for actions like run, edit, delete, clone, and move.
 *
 * @param {Object} props - Component props
 * @param {Array} props.testCases - Array of test case objects to display (required)
 * @param {Array} props.selectedTestCases - Array of selected test case IDs (required)
 * @param {Function} props.onSelectionChange - Callback when selection changes (required)
 * @param {Function} props.onEdit - Callback when edit action is triggered (required)
 * @param {Function} props.onDelete - Callback when delete action is triggered (required)
 * @param {Function} props.onClone - Callback when clone action is triggered (required)
 * @param {Function} props.onMove - Callback when move action is triggered (required)
 * @param {Function} props.onView - Callback when view action is triggered (required)
 * @param {Function} [props.onRun] - Callback when run action is triggered
 * @param {string} [props.role] - User role to determine available actions
 */
const HostingTable = ({
  testCases,
  selectedTestCases,
  onSelectionChange,
  onEdit,
  onDelete,
  onClone,
  onMove,
  onView,
  onRun,
  role,
}) => {
  const columns = [
    {
      field: 'displayname',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const original = params.row;
        const onChangeFn = original.disabled
          ? () => {}
          : () => onView(original);

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
            }}
          >
            <Link
              component="button"
              onClick={onChangeFn}
              underline="hover"
              title={original.displayname || ''}
              sx={{
                textAlign: 'left',
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {original.displayname}
            </Link>
            {original.isGitSourceDeleted && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginLeft: '8px',
                  padding: '2px 8px',
                  backgroundColor: 'error.light',
                  color: 'error.main',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
                title="This test case was deleted from Git source"
              >
                <span role="img" aria-label="Warning">
                  ⚠️
                </span>{' '}
                Deleted from Git
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 300,
      renderCell: (params) => {
        const description = params.value || '';
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            title={description}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {description}
          </Typography>
        );
      },
    },
    {
      field: 'modifieddate',
      headerName: 'Last Modified',
      flex: 0.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.value).toLocaleString()}
        </Typography>
      ),
    },
  ];

  columns.push({
    field: 'actions',
    headerName: 'Actions',
    flex: 0.3,
    minWidth: role === 'admin' ? 180 : 40,
    sortable: false,
    renderCell: (params) => {
      const original = params.row;
      const iconButtonStyle = {
        color: 'text.primary',
        paddingTop: '4px',
        paddingBottom: '4px',
        paddingLeft: '0px',
        paddingRight: '0px',
        minWidth: '30px',
        minHeight: '30px',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      };

      return (
        <Box sx={{ display: 'flex', gap: '0px' }}>
          {onRun && (
            <Tooltip title="Run">
              <IconButton
                onClick={() => onRun(original)}
                size="small"
                sx={iconButtonStyle}
              >
                <PlayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {role === 'admin' && (
            <>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => onEdit(original)}
                  size="small"
                  sx={iconButtonStyle}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => onDelete(original)}
                  size="small"
                  sx={iconButtonStyle}
                >
                  <TrashIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clone">
                <IconButton
                  onClick={() => onClone(original)}
                  size="small"
                  sx={iconButtonStyle}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Move">
                <IconButton
                  onClick={() => onMove(original)}
                  size="small"
                  sx={iconButtonStyle}
                >
                  <MoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      );
    },
  });

  return (
    <Box sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
      <DataGrid
        rows={testCases}
        columns={columns}
        checkboxSelection
        disableSelectionOnClick
        rowSelectionModel={selectedTestCases}
        onRowSelectionModelChange={onSelectionChange}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
            borderTop: 'none !important',
            fontSize: '0.875rem',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row .MuiDataGrid-cell': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
            borderTop: 'none !important',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#ffffff !important',
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: 'text.secondary',
            '& .MuiDataGrid-columnHeader': {
              padding: '16px',
              borderRight: 'none !important',
              borderLeft: 'none !important',
              backgroundColor: '#ffffff !important',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none !important',
            },
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
            backgroundColor: '#ffffff !important',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            '& .MuiDataGrid-cell': {
              borderRight: 'none !important',
              borderLeft: 'none !important',
            },
          },
          '& .MuiDataGrid-row': {
            borderBottom: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer !important',
            '&:hover': {
              backgroundColor: 'grey.50',
              cursor: 'pointer !important',
            },
            '&:last-child': {
              borderBottom: 'none',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
            justifyContent: 'space-between',
            padding: '16px',
          },
        }}
      />
    </Box>
  );
};

export default HostingTable;
